import React from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Stack,
    Link,
    Divider,
    Tooltip,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import BadgeIcon from '@mui/icons-material/Badge';
import SchoolIcon from '@mui/icons-material/School';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

const SECTIONS = [
    {
        title: 'Adresses e-mail',
        items: [
            {
                icon: <EmailIcon />,
                label: 'Université Montpellier — Principal',
                value: 'benoit.lange@umontpellier.fr',
                link: 'mailto:benoit.lange@umontpellier.fr',
                color: '#00695c',
            },
            {
                icon: <AlternateEmailIcon />,
                label: 'LIRMM',
                value: 'benoit.lange@lirmm.fr',
                link: 'mailto:benoit.lange@lirmm.fr',
                color: '#1a237e',
            },
            {
                icon: <AlternateEmailIcon />,
                label: 'INRIA',
                value: 'benoit.lange@inria.fr',
                link: 'mailto:benoit.lange@inria.fr',
                color: '#c62828',
            },
        ],
    },
    {
        title: 'Affiliation & laboratoire',
        items: [
            {
                icon: <SchoolIcon />,
                label: 'Laboratoire',
                value: 'LIRMM — Équipe IROKO',
                subValue: 'Université de Montpellier / INRIA',
                color: '#6a1b9a',
            },
            {
                icon: <BadgeIcon />,
                label: 'Identifiant ORCID',
                value: '0009-0007-7820-8249',
                link: 'https://orcid.org/0009-0007-7820-8249',
                color: '#2e7d32',
                external: true,
            },
            {
                icon: <LocationOnIcon />,
                label: 'Bureau',
                value: '161 rue Ada, Montpellier',
                subValue: '34392 Cedex 5 — France',
                color: '#e65100',
            },
        ],
    },
];

export default function Contact() {
    return (
        <Box sx={{
            width: '100%',
            py: { xs: 2, md: 3 },
            px: { xs: 1, md: 2 },
        }}>
            <Card sx={{
                width: '100%',
                borderRadius: 3,
                overflow: 'hidden',
                boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
            }}>
                <CardContent sx={{ p: { xs: 3, md: 5 } }}>
                    <Stack spacing={4} divider={<Divider flexItem />}>
                        {SECTIONS.map((section) => (
                            <Box key={section.title}>
                                <Typography variant="overline" sx={{
                                    fontWeight: 700,
                                    color: 'primary.main',
                                    letterSpacing: 1.5,
                                    display: 'block',
                                    mb: 2,
                                }}>
                                    {section.title}
                                </Typography>
                                <Stack spacing={1.5}>
                                    {section.items.map((item) => (
                                        <ContactRow key={item.label} {...item} />
                                    ))}
                                </Stack>
                            </Box>
                        ))}
                    </Stack>
                </CardContent>
            </Card>
        </Box>
    );
}

function ContactRow({ icon, label, value, subValue, link, color, external }) {
    const content = (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            px: 2,
            py: 1.5,
            borderRadius: 2,
            borderLeft: `3px solid ${color}`,
            bgcolor: '#fafbfc',
            transition: 'background 0.15s',
            '&:hover': link ? { bgcolor: '#f0f4ff' } : {},
        }}>
            <Box sx={{ color, flexShrink: 0, display: 'flex', fontSize: '1.2rem' }}>
                {icon}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="caption" sx={{ color: 'text.disabled', fontWeight: 600, letterSpacing: 0.5 }}>
                    {label}
                </Typography>
                {link ? (
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Link
                            href={link}
                            underline="hover"
                            target={external ? '_blank' : undefined}
                            rel={external ? 'noopener noreferrer' : undefined}
                            sx={{ fontWeight: 600, color: '#1a1a2e', fontSize: '0.92rem' }}
                        >
                            {value}
                        </Link>
                        {external && <OpenInNewIcon sx={{ fontSize: 13, color: 'text.disabled' }} />}
                    </Stack>
                ) : (
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#1a1a2e' }}>
                        {value}
                    </Typography>
                )}
                {subValue && (
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                        {subValue}
                    </Typography>
                )}
            </Box>
        </Box>
    );

    return external ? (
        <Tooltip title="Ouvrir dans un nouvel onglet" placement="right">
            {content}
        </Tooltip>
    ) : content;
}